
import Vue from 'vue';
import Component from 'vue-class-component';
import {Emit, Prop, Watch} from "vue-property-decorator";
import Property from "@/components/properties/model/Property";
import {AXIOS} from "@/lib/http-common";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import {i18n} from "@/main";

@Component({})
export default class PropertyField extends Vue {
  @Prop({default: () => [], required: false})
  public predefinedSymbols!: string[];
  @Prop({default: '', required: false})
  public label!: string;
  @Prop({default: '', required: true})
  public propertyId!: string;
  @Prop({default: false, required: false})
  public checkIsEmpty!: boolean;
  @Prop({default: false, required: false})
  public checkIsPositiveNumber!: boolean;
  @Prop({default: null, required: false})
  public customValidation!: (valueToValidate: any) => string | boolean;
  @Prop({default: 'text', required: false})
  public type!: string;
  @Prop({default: '', required: false})
  public hint!: string;
  @Prop({default: () => [], required: false})
  public valueSelectOptions!: string[];
  @Prop({default: () => [], required: false})
  public valueComboBoxOptions!: string[];
  @Prop({default: () => [], required: false})
  public valueRadioOptions!: string[];
  @Prop({default: false, required: false})
  public isSwitch!: boolean;
  @Prop({default: true, required: false})
  public isEnabled!: boolean;
  @Prop({default: false, required: false})
  public dense!: boolean;
  @Prop({default: false, required: false})
  public clearable!: boolean;
  @Prop({default: "/scale", required: false})
  public apiUrl!: string;
  @Prop({default: null, required: false})
  public regexPattern!: RegExp;

  public value: any = '';
  public switchValue: boolean = false;
  public loading: boolean = false;
  public updating: boolean = false;
  public loadingError: boolean = false;

  get isLoading() {
    return this.loading || this.updating;
  }

  get isDisabled() {
    return this.loading || this.loadingError || !this.isEnabled;
  }

  public mounted() {
    this.loading = true;

    AXIOS.get(`/properties/${this.propertyId}`)
        .then((message) => {
          this.value = message.data.propValue;
          if (this.isSwitch) {
            this.switchValue = this.value === 'true';
          }
          this.emitValueLoaded(this.value);
        })
        .catch(error => {
          UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
          this.loadingError = true;
        })
        .finally(() => {
          this.loading = false;
        });
  }

  public validateValue(valueToValidate: any): string | boolean {
    if (this.checkIsEmpty && (valueToValidate === null || valueToValidate === '')) {
      return i18n.tc('global.rules.emptyFieldIsNotAllowed');
    }

    if (this.type === 'number' && this.checkIsPositiveNumber && this.value < 0) {
      return i18n.tc('global.rules.negativeNumbersAreNotAllowed');
    }

    if (this.customValidation !== null) {
      return this.customValidation(valueToValidate);
    }

    return true;
  }

  @Watch('switchValue')
  public onSwitchValueChanged(): void {
    this.value = this.switchValue;
  }

  @Watch('value')
  public async onValueChanged(newValue: string): Promise<void> {
    if (this.loading) {
      return;
    }

    if (newValue === null || newValue === undefined) {
      this.value = '';
      return;
    }

    const validationResult = this.validateValue(newValue);

    if (validationResult !== true) {
      return;
    }

    await this.updateProperty();

    this.emitValueChanged(newValue);
  }

  @Emit('value-changed')
  public emitValueChanged(value: string) {
    return value;
  }

  @Emit('value-loaded')
  public emitValueLoaded(value: string) {
    return value;
  }

  public async updateProperty(): Promise<void> {
    this.updating = true;
    const propertyDto: Property = {propKey: this.propertyId, propValue: this.value};
    await AXIOS.put(`/properties${this.apiUrl}`, propertyDto)
        .catch(error => {
          UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
        })
        .finally(() => {
          this.updating = false;
        });
  }

  public insertSpecialSymbol(symbol: string): void {
    if (this.value) {
      this.value = this.value + symbol;
    } else {
      this.value = symbol;
    }
  }
}
